import { Viewport } from "pixi-viewport";
import { Cluster } from "../interfaces";
import { useCallback, useEffect, useState } from "react";
import { calculateBoundingBox } from "../utils/math";

// Constants
const COVERAGE_THRESHOLD = 0.1; // 10% coverage threshold


/**
 * Optimized hook for checking active cluster coverage
 * @param {Object} params - Configuration parameters
 * @returns {Object} Active cluster state management utilities
 */
export const useActiveClusterCoverage = ({
  viewportRef,
  clusters,
  dimensions,
  onActiveClusterChange,
}: {
  viewportRef: React.RefObject<Viewport>;
  clusters: Cluster[];
  dimensions: { width: number; height: number };
  onActiveClusterChange?: (cluster: Cluster | null) => void;
}) => {
  // Memoize the coverage calculation to reduce unnecessary recalculations
  const findActiveCluster = useCallback(() => {
    if (!viewportRef.current || !clusters.length) return null;

    const { current: viewport } = viewportRef;
    const { width: screenWidth, height: screenHeight } = dimensions;

    const viewportBounds = {
      minX: 0,
      minY: 0,
      maxX: screenWidth,
      maxY: screenHeight,
    };

    let activeCluster: Cluster | null = null;
    let maxCoverage = 0;

    for (let i = 0; i < clusters.length; i++) {
      const cluster = clusters[i];
      const boundingBox = calculateBoundingBox(cluster);
      if (
        boundingBox.minX === boundingBox.maxX ||
        boundingBox.minY === boundingBox.maxY
      ) {
        continue;
      }

      const screenCorners = [
        viewport.toScreen(boundingBox.minX, boundingBox.minY),
        viewport.toScreen(boundingBox.maxX, boundingBox.minY),
        viewport.toScreen(boundingBox.minX, boundingBox.maxY),
        viewport.toScreen(boundingBox.maxX, boundingBox.maxY),
      ];

      const minX = Math.min(
        screenCorners[0].x,
        screenCorners[1].x,
        screenCorners[2].x,
        screenCorners[3].x,
      );
      const maxX = Math.max(
        screenCorners[0].x,
        screenCorners[1].x,
        screenCorners[2].x,
        screenCorners[3].x,
      );
      const minY = Math.min(
        screenCorners[0].y,
        screenCorners[1].y,
        screenCorners[2].y,
        screenCorners[3].y,
      );
      const maxY = Math.max(
        screenCorners[0].y,
        screenCorners[1].y,
        screenCorners[2].y,
        screenCorners[3].y,
      );

      // Calculate intersection of cluster screen bounds with viewport bounds
      const intersectionMinX = Math.max(minX, viewportBounds.minX);
      const intersectionMaxX = Math.min(maxX, viewportBounds.maxX);
      const intersectionMinY = Math.max(minY, viewportBounds.minY);
      const intersectionMaxY = Math.min(maxY, viewportBounds.maxY);

      const intersectionWidth = intersectionMaxX - intersectionMinX;
      const intersectionHeight = intersectionMaxY - intersectionMinY;

      if (intersectionWidth <= 0 || intersectionHeight <= 0) {
        continue; // No intersection
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const coverage = intersectionArea / (screenWidth * screenHeight);

      if (coverage > maxCoverage && coverage >= COVERAGE_THRESHOLD) {
        activeCluster = cluster;
        maxCoverage = coverage;
      }
    }

    return activeCluster;
  }, [clusters, dimensions, viewportRef]);

  // Use state to track and manage active cluster
  const [activeClusterId, setActiveClusterId] = useState<string | null>(null);

  // Effect to update active cluster and trigger change handler
  useEffect(() => {
    const activeCluster = findActiveCluster();

    // Only update if the active cluster has changed
    const newActiveClusterId = activeCluster?.id ?? null;
    if (newActiveClusterId !== activeClusterId) {
      setActiveClusterId(newActiveClusterId);
      onActiveClusterChange?.(activeCluster);
    }
  }, [findActiveCluster, activeClusterId, onActiveClusterChange]);

  return {
    activeClusterId,
    activeCluster: clusters.find((cluster) => cluster.id === activeClusterId) ?? null,
  };
};
